import React, { useEffect, useRef, useState } from 'react';
import { Chart, ChartWrapperOptions, GoogleDataTableColumnRoleType, GoogleDataTableColumnType, ReactGoogleChartEvent } from 'react-google-charts';
import { IntervalChartSettings } from '@mono/types/src/interval-chart';
import { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { useMDSContext } from '@metservice/use-mds';
import { convertValueUnit } from '@metservicedev/cf-units';
import useSize from '@react-hook/size';
import { DateTime, Duration } from 'luxon';
import styled from 'styled-components';
import { getDatabilityIndex } from '@metservicedev/platforms-services-interface/src';
import { Capability } from '@mono/types/src/mds';
import { toHash } from '@mono/to-hash';

const ZGoogleChart = styled(Chart)`
    .google-visualization-tooltip {
        z-index: 9999 !important;
    }
`;

const customTooltip = (time: DateTime, value: number | number[], unit: string, label: string) => {
    return `<div style="white-space: nowrap; padding-left: 5px; padding-bottom: 5px; padding-right: 5px">
			<strong>${time.toFormat('ccc dd MMM HH:mm')}</strong>
			<br />
			${
                Array.isArray(value)
                    ? `<div>
						<strong>Mean</strong>: ${value[0] + unit}
						<br />
						<strong>Upper CI:</strong> ${value[1] + unit}
						<br />
						<strong>Lower CI:</strong> ${value[2] + unit}
					</div>`
                    : `<strong>${label}</strong>: ${value + unit}`
            }
		</div>`;
};

const flipValue180 = (sourceUnit: string, value: number | string) => {
    const degrees = convertValueUnit({ from: sourceUnit, to: 'deg', value });
    const degreeValue = parseFloat(degrees.toString());
    const normalisedValue = +((((degreeValue + 180) % 360) + 360) % 360);
    return convertValueUnit({ from: 'deg', to: sourceUnit, value: normalisedValue });
};

export const IntervalChart = ({ settings, cfjsons }: WeatherWidgetRenderProps<IntervalChartSettings>) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const [width, height] = useSize(chartRef);
    const { capabilities } = useMDSContext();

    const [rawData, setRawData] = useState<{
        timeData: DateTime[];
        meanData: number[];
        upperCIData: number[];
        lowerCIData: number[];
        highData: number[];
        lowData: number[];
        directionalData: number[];
    }>();

    const [sourceUnits, setSourceUnits] = useState('m/s');

    const [data, setData] = useState<(string | number | undefined)[][]>([[]]);

    const {
        title,
        fontSize,
        leftOffset,
        yAxisLabel,
        unit,
        unitLabel,
        interval,
        intervalType,
        thresholds,
        showHighestThreshold,
        databilities,
        mean,
        high,
        low,
        direction,
        timezone,
    } = settings;

    const fontSizeToUse = fontSize || 100;
    const arrowSize = direction?.arrowSize ?? 1;
    const arrowColour = direction?.colour ?? '#000000';

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [validTimes, setValidTimes] = useState<number[]>([]);
    const [gridLines, setGridLines] = useState<number[]>([]);
    const [directionalTimes, setDirectionalTimes] = useState<number[]>([]);

    const selectedMeanCapability = capabilities.find(({ id }) => id === databilities.find(({ id }) => id === 'mean')?.capabilityId);
    const selectedUpperCICapability = capabilities.find(({ id }) => id === databilities.find(({ id }) => id === 'upper')?.capabilityId);
    const selectedLowerCICapability = capabilities.find(({ id }) => id === databilities.find(({ id }) => id === 'lower')?.capabilityId);
    const selectedHighCapability = capabilities.find(({ id }) => id === databilities.find(({ id }) => id === 'high')?.capabilityId);
    const selectedLowCapability = capabilities.find(({ id }) => id === databilities.find(({ id }) => id === 'low')?.capabilityId);
    const selectedDirectionalCapability = capabilities.find(({ id }) => id === databilities.find(({ id }) => id === 'directional')?.capabilityId);

    useEffect(() => {
        if (!databilities || capabilities.length === 0) return;
        if (!cfjsons) return;

        const databilityIndex = getDatabilityIndex(databilities[0]);
        const cfjson = cfjsons.find((cjfsonResponse) => databilityIndex === cjfsonResponse.databilityIndex)?.cfjson;

        const variables = cfjson?.variables;

        if (!variables) return;

        const getAnyVariableFromCapability = (capability: Capability | undefined) => variables[`${Object.keys(capability?.variables ?? {})[0]}`];
        const getDataArrayFromCapability = (capability: Capability | undefined) => getAnyVariableFromCapability(capability)?.data ?? [];

        const mdsDirection = Object.values(selectedDirectionalCapability?.variables ?? {})[0]?.attributes.direction;
        const mdsDirectionUnit = Object.values(selectedDirectionalCapability?.variables ?? {})[0]?.units || '';
        const shouldFlip180 = typeof mdsDirection === 'string' && mdsDirection !== 'to';

        const directionalData = shouldFlip180
            ? getDataArrayFromCapability(selectedDirectionalCapability).map((data) => flipValue180(mdsDirectionUnit, data))
            : getDataArrayFromCapability(selectedDirectionalCapability);

        setSourceUnits(getAnyVariableFromCapability(selectedMeanCapability)?.attributes?.units);
        setRawData({
            timeData: variables['time'].data.map((dateTime: string) => DateTime.fromISO(dateTime).setZone(targetTimezone)),
            meanData: getDataArrayFromCapability(selectedMeanCapability),
            upperCIData: getDataArrayFromCapability(selectedUpperCICapability),
            lowerCIData: getDataArrayFromCapability(selectedLowerCICapability),
            highData: getDataArrayFromCapability(selectedHighCapability),
            lowData: getDataArrayFromCapability(selectedLowCapability),
            directionalData: directionalData,
        });
    }, [toHash(cfjsons)]);

    useEffect(() => {
        if (!rawData) return;
        if (!databilities[0].timeRange) return;
        const { timeData, meanData, upperCIData, lowerCIData, highData, lowData } = rawData;

        const timeRange = databilities[0].timeRange.split('--').map((t) => Duration.fromISO(t).get('days'));

        const start = DateTime.now().setZone(targetTimezone).startOf('day').plus({ days: timeRange[0] });
        const end = DateTime.now().setZone(targetTimezone).startOf('day').plus({ days: timeRange[1] });

        const validTimes = timeData.flatMap((time, index) => {
            if (time >= start && time <= end) {
                return index;
            } else {
                return [];
            }
        });
        setValidTimes(validTimes);

        const validTimesArray = [...validTimes];
        const gridLines = validTimesArray.map((index, _, arr) => index - arr[0]).filter((index) => index % interval === 0);
        setGridLines(gridLines);
        const arrowLabelFrequency = validTimesArray.map((index, _, arr) => index - arr[0]).filter((index) => index % (direction?.arrowFrequency || 1) === 0);
        setDirectionalTimes(arrowLabelFrequency);

        if ([...meanData, ...upperCIData, ...lowerCIData, ...highData, ...lowData].length <= 0) return;

        const maxValue = [meanData, upperCIData, lowerCIData, highData, lowData]
            .map((dataset) => dataset?.filter((_, index) => validTimes.includes(index)).reduce((max, value) => Math.max(max, value), 0))
            .reduce((max, value) => {
                if (!value) return max;
                return Math.max(max, value);
            }, Number.NEGATIVE_INFINITY);

        const conversionUnit = unit && unit !== '' && unit.length > 0 ? unit : sourceUnits;
        try {
            const highValue = convertValueUnit({ from: sourceUnits, to: conversionUnit, value: maxValue, decimalPrecision: 3 }) as number;
            const scale = 5 * Math.pow(10, Math.round(highValue).toString().length - 2);

            const thresholdValues =
                thresholds?.map(({ number }) => {
                    const scaledValue = Math.ceil(highValue / scale) * scale;
                    if (showHighestThreshold || number <= scaledValue) return number;
                    return -1;
                }) ?? [];

            const data = validTimesArray.map((index, i) => {
                const dateTimeObject: DateTime = timeData[index];
                function convertValue<T>(data: number[], unknownValue: T) {
                    return data?.[index] !== undefined
                        ? (convertValueUnit({ from: sourceUnits, to: conversionUnit, value: data[index], decimalPrecision: 3 }) as number)
                        : unknownValue;
                }
                return [
                    i + 1,
                    convertValue(meanData, undefined),
                    customTooltip(
                        dateTimeObject,
                        [convertValue(meanData, 0), convertValue(upperCIData, 0), convertValue(lowerCIData, 0)],
                        conversionUnit,
                        mean.label || 'Mean'
                    ),
                    convertValue(upperCIData, undefined),
                    convertValue(lowerCIData, undefined),
                    convertValue(highData, undefined),
                    customTooltip(dateTimeObject, convertValue(highData, 0), conversionUnit, high.label || 'High'),
                    convertValue(lowData, undefined),
                    customTooltip(dateTimeObject, convertValue(lowData, 0), conversionUnit, low.label || 'Low'),
                    ...thresholdValues,
                ];
            });
            setData(data);
        } catch (e) {
            console.warn(`Could not set data due to conversion error: ${e}`);
        }
    }, [rawData, interval, intervalType, unit, thresholds, direction, showHighestThreshold]);

    interface googleChartColumns {
        type: GoogleDataTableColumnType;
        id?: string;
        label?: string;
        role?: GoogleDataTableColumnRoleType;
        p?: { html: boolean };
    }

    const columns: googleChartColumns[] = [
        { type: 'number', label: 'time' },
        { type: 'number', label: mean.label },
        { type: 'string', role: GoogleDataTableColumnRoleType.tooltip, p: { html: true } },
        {
            type: 'number',
            id: 'i0',
            role: GoogleDataTableColumnRoleType.interval,
        },
        {
            type: 'number',
            id: 'i0',
            role: GoogleDataTableColumnRoleType.interval,
        },
        { type: 'number', label: high.label },
        { type: 'string', role: GoogleDataTableColumnRoleType.tooltip, p: { html: true } },
        { type: 'number', label: low.label },
        { type: 'string', role: GoogleDataTableColumnRoleType.tooltip, p: { html: true } },
        ...Array(thresholds.length)
            .fill(null)
            .map(() => {
                return { type: 'number' } as { type: GoogleDataTableColumnType };
            }),
    ];

    const tableData =
        data && data.length >= 2 && data[1].length === data[0].length
            ? [columns, ...data]
            : [
                  [
                      { type: 'number', label: '' },
                      { type: 'number', label: '' },
                      { type: 'number', label: '' },
                      { type: 'number', label: '' },
                      { type: 'number', label: '' },
                      { type: 'number', label: '' },
                  ],
                  [0, 0, 0, 0, 0, 0],
              ];

    const series = [
        {
            type: 'line',
            color: mean.colour,
            lineWidth: mean.lineWidth,
            pointSize: mean.pointSize,
            visibleInLegend: !!databilities.find(({ id }) => id === 'mean')?.capabilityId,
        },
        {
            type: 'line',
            color: high.colour,
            lineWidth: high.lineWidth,
            pointSize: high.pointSize,
            visibleInLegend: !!databilities.find(({ id }) => id === 'high')?.capabilityId,
        },
        {
            type: 'line',
            color: low.colour,
            lineWidth: low.lineWidth,
            pointSize: low.pointSize,
            visibleInLegend: !!databilities.find(({ id }) => id === 'low')?.capabilityId,
        },
        ...thresholds.map((threshold) => {
            return {
                lineWidth: 1,
                type: 'line',
                visibleInLegend: false,
                enableInteractivity: false,
                color: threshold?.colour?.hex,
                pointSize: 0, //removes error message
                lineDashStyle: [5, 5],
            };
        }),
    ];

    const ticks = gridLines.flatMap((i) => {
        const index = validTimes[i];
        if (rawData?.timeData[index]) {
            const dateTimeObject: DateTime = rawData?.timeData[index];
            return {
                v: index - validTimes[0] + 1,
                f: dateTimeObject.hour === 0 ? dateTimeObject.toFormat('ccc dd') : dateTimeObject.toFormat('HH:mm'),
            };
        } else return [];
    });

    const options: ChartWrapperOptions['options'] = {
        title,
        titleTextStyle: { fontSize: 14 * (fontSizeToUse / 100) },
        series,
        interval: {
            i0: { style: intervalType, lineWidth: mean.lineWidth, pointSize: mean.pointSize },
        },
        legend: {
            position: 'top',
            alignment: 'start',
            textStyle: { fontSize: 14 * (fontSizeToUse / 100) },
        },
        width,
        height,
        vAxis: {
            title: yAxisLabel,
            titleTextStyle: { italic: false, fontSize: 14 * (fontSizeToUse / 100) },
            textStyle: { fontSize: 12.6 * (fontSizeToUse / 100) },
            gridlines: { multiple: 5, color: '#eee' },
            baselineColor: 'transparent',
            minorGridlines: { count: 0 },
            maxValue: showHighestThreshold && thresholds[0] && thresholds[0].number ? thresholds[0].number : 'automatic',
            viewWindow: { min: 0 },
        },
        hAxis: {
            //@ts-ignore - this is a bug in the google charts library
            ticks,
            textStyle: { fontSize: 12.6 * (fontSizeToUse / 100) },
            slantedText: false,
            maxAlternation: 1,
            allowContainerBoundaryTextCutoff: true,
            viewWindow: { min: 1, max: validTimes.length },
            baselineColor: 'transparent',
            gridlines: { color: '#eee' },
        },
        chartArea: { left: leftOffset, top: 75, height: height - 150, width: width - leftOffset },
        isStacked: true,
        tooltip: { isHtml: true },
    };

    const formatters = [
        {
            type: 'NumberFormat' as const,
            column: 1,
            options: {
                pattern: `#.#${unitLabel}`,
            },
        },
        {
            type: 'NumberFormat' as const,
            column: 2,
            options: {
                pattern: `#.#${unitLabel}`,
            },
        },
        {
            type: 'NumberFormat' as const,
            column: 3,
            options: {
                pattern: `#.#${unitLabel}`,
            },
        },
        {
            type: 'NumberFormat' as const,
            column: 4,
            options: {
                pattern: `#.#${unitLabel}`,
            },
        },
        {
            type: 'NumberFormat' as const,
            column: 5,
            options: {
                pattern: `#.#${unitLabel}`,
            },
        },
    ];

    const chartEvents: ReactGoogleChartEvent[] = [
        {
            eventName: 'ready',
            callback: ({ eventArgs }) => {
                if (!rawData) return;
                const pointObjects = eventArgs[0].ls.o2;
                const entries = Object.entries(pointObjects);
                const xyValues = entries.map((kv: any) => {
                    const line = kv[0].split('#')[1];
                    return { line, xValue: kv[1].center.x, yValue: kv[1].center.y };
                });

                const { directionalData } = rawData;
                for (let i = 0; i <= directionalTimes?.length; i++) {
                    const element = document.getElementById(`direction#${i}`) as any as SVGElement;
                    if (!element) return;
                    const direction = directionalData[i] || 0;
                    element.setAttribute(
                        'transform',
                        `translate(${xyValues[directionalTimes[i]].xValue} ${xyValues[directionalTimes[i]].yValue}) rotate(${direction})`
                    );
                    element.style.display = '';
                }
            },
        },
        {
            eventName: 'error',
            callback: ({ google, chartWrapper }) => {
                //@ts-ignore  - this is a bug in the google charts library
                google.visualization.errors.removeAll(chartWrapper.getChart().container);
            },
        },
    ];

    const WindDirections = (directionalData: number[]): JSX.Element[] => {
        if (!directionalData || directionalData.length <= 0 || !validTimes || validTimes.length <= 0) return [];

        return directionalTimes?.map((_, index) => (
            <g key={`direction#${index}`} id={`direction#${index}`}>
                <polygon
                    points={`${-3 * arrowSize} ${-5 * arrowSize}, ${0 * arrowSize} ${-11 * arrowSize}, ${3 * arrowSize} ${-5 * arrowSize}`}
                    stroke={arrowColour === 'black' || arrowColour === '#000000' ? '#595959' : '#000'}
                    strokeWidth={1 * arrowSize}
                    fill={arrowColour}
                />
                <line
                    x1={0 * arrowSize}
                    x2={0 * arrowSize}
                    y1={-5 * arrowSize}
                    y2={5 * arrowSize}
                    stroke={arrowColour === 'black' || arrowColour === '#000000' ? '#595959' : '#000'}
                    strokeWidth={3 * arrowSize}
                />
                <line x1={0 * arrowSize} x2={0 * arrowSize} y1={-6 * arrowSize} y2={4 * arrowSize} stroke={arrowColour} strokeWidth={1 * arrowSize} />
            </g>
        ));
    };

    return (
        <div style={{ width: '100%', height: '100%', minWidth: 400, minHeight: 200, position: 'relative' }}>
            <div ref={chartRef}>
                <ZGoogleChart data={tableData} chartType={'LineChart'} options={options} formatters={formatters} chartEvents={chartEvents} />
                <svg style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
                    {WindDirections(rawData?.directionalData ?? [])}
                </svg>
            </div>
        </div>
    );
};
